import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { MapPin, Users, Layers, HelpCircle, Phone, Home, Map as MapIcon } from 'lucide-react';
import ClientsMap from '@/components/ClientsMap';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Zones() {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState({ zones: [], total_zones: 0, unassigned_count: 0 });
  const [loading, setLoading] = useState(true);
  const [mapZone, setMapZone] = useState(null);

  const fetchZones = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/clients/zones`, getAuthHeaders());
      setData(response.data);
    } catch (error) {
      console.error('Error fetching zones:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchZones();
  }, []);

  const zoneLabel = (zone) => {
    if (zone.code_postal && zone.ville && zone.ville !== 'Sans adresse') {
      return `${zone.code_postal} ${zone.ville}`;
    }
    return zone.ville || zone.code_postal || 'Sans adresse';
  };

  const clientAddress = (c) => {
    const parts = [];
    if (c.adresse) parts.push(c.adresse);
    const cpVille = [c.code_postal, c.ville].filter(Boolean).join(' ');
    if (cpVille) parts.push(cpVille);
    return parts.join(', ');
  };

  const realZones = data.zones.filter((z) => z.ville !== 'Sans adresse');
  const largestZone = realZones.length > 0 ? realZones.reduce((a, b) => (b.client_count > a.client_count ? b : a)) : null;

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="zones-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Regroupements / Zones</h1>
        <p className="text-muted-foreground mt-2">
          Vos client·e·s regroupé·e·s par zone (code postal / ville) pour planifier vos tournées
        </p>
      </div>

      {/* Statistiques */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="rounded-xl border-border/60" data-testid="stat-total-zones">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <Layers className="w-6 h-6 text-primary" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Zones</p>
              <p className="text-2xl font-bold">{data.total_zones}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-xl border-border/60" data-testid="stat-largest-zone">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
              <MapPin className="w-6 h-6 text-primary" strokeWidth={1.5} />
            </div>
            <div className="min-w-0">
              <p className="text-sm text-muted-foreground">Zone principale</p>
              <p className="text-lg font-bold truncate">
                {largestZone ? `${zoneLabel(largestZone)} (${largestZone.client_count})` : '—'}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card className="rounded-xl border-border/60" data-testid="stat-unassigned">
          <CardContent className="p-6 flex items-center gap-4">
            <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center shrink-0">
              <HelpCircle className="w-6 h-6 text-muted-foreground" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Sans adresse</p>
              <p className="text-2xl font-bold">{data.unassigned_count}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Liste des zones */}
      {loading ? (
        <Card className="rounded-xl border-border/60">
          <CardContent className="flex items-center justify-center py-12">
            <p className="text-muted-foreground">Chargement…</p>
          </CardContent>
        </Card>
      ) : data.zones.length === 0 ? (
        <Card className="rounded-xl border-border/60">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <MapPin className="w-16 h-16 text-muted-foreground mb-4" strokeWidth={1} />
            <p className="text-muted-foreground">Aucun·e client·e pour le moment</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
          {data.zones.map((zone, idx) => {
            const isNone = zone.ville === 'Sans adresse';
            return (
              <Card
                key={`${zone.code_postal}-${zone.ville}-${idx}`}
                className={`rounded-xl border-border/60 ${isNone ? 'bg-muted/20' : ''}`}
                data-testid={`zone-card-${idx}`}
              >
                <CardHeader className="pb-3">
                  <div className="flex items-center justify-between gap-3">
                    <CardTitle className="text-lg flex items-center gap-2 min-w-0">
                      <MapPin className={`w-5 h-5 shrink-0 ${isNone ? 'text-muted-foreground' : 'text-primary'}`} strokeWidth={1.5} />
                      <span className="truncate">{zoneLabel(zone)}</span>
                    </CardTitle>
                    <div className="flex items-center gap-2 shrink-0">
                      {!isNone && zone.clients.some((c) => c.latitude !== null && c.latitude !== undefined) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setMapZone(zone)}
                          data-testid={`btn-map-zone-${idx}`}
                        >
                          <MapIcon className="w-4 h-4 mr-1" />
                          Carte
                        </Button>
                      )}
                      <Badge variant={isNone ? 'outline' : 'default'}>
                        <Users className="w-3.5 h-3.5 mr-1" />
                        {zone.client_count}
                      </Badge>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  {zone.clients.map((c) => (
                    <div
                      key={c.id}
                      className="p-3 rounded-lg bg-muted/30 border border-border/40"
                      data-testid={`zone-client-${c.id}`}
                    >
                      <p className="font-medium">{c.prenom} {c.nom}</p>
                      {c.telephone && (
                        <p className="text-sm text-muted-foreground flex items-center gap-1.5 mt-1">
                          <Phone className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5} />
                          {c.telephone}
                        </p>
                      )}
                      {clientAddress(c) && (
                        <p className="text-sm text-muted-foreground flex items-center gap-1.5 mt-1">
                          <Home className="w-3.5 h-3.5 shrink-0" strokeWidth={1.5} />
                          {clientAddress(c)}
                        </p>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!mapZone} onOpenChange={(o) => !o && setMapZone(null)}>
        <DialogContent className="max-w-3xl w-[95vw]" data-testid="zone-map-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MapIcon className="w-5 h-5 text-primary" strokeWidth={1.5} />
              {mapZone ? zoneLabel(mapZone) : ''}
            </DialogTitle>
            <DialogDescription>
              Localisation des client·e·s de cette zone
            </DialogDescription>
          </DialogHeader>
          {mapZone && <ClientsMap clients={mapZone.clients} height="500px" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
